// AboutPage.jsx
//
// "About us" — mission, values, why-we-built-this. Intentionally short
// and human. Three pillars (Story / Values / Where we're going), then
// a CTA back to Book a Demo.

import React from 'react';
import {
  ArrowRight, Heart, Compass, Sparkles, Scale, ShieldCheck,
  Globe2, Code2, BookOpen,
} from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';
import { WatchDemoButton } from './WatchDemoModal';

const VALUES = [
  {
    icon: Scale,
    title: 'Built by people who respect the practice',
    body: 'We talk to attorneys every week. We don\'t guess at how a case file should be structured — we ask, and we change the product when the answer surprises us.',
  },
  {
    icon: ShieldCheck,
    title: 'Security is a feature, not a checkbox',
    body: 'Row-level security on every table. End-to-end encryption on messages. Tamper-evident audit trails for high-stakes records. Your data is yours, isolated from every other firm on the platform.',
  },
  {
    icon: Globe2,
    title: 'Made for Ghana — priced for Ghana',
    body: 'Cedis on every invoice. VAT and NHIL lines built in. Mobile money support coming. We\'re not a US product translated for emerging markets — we built this here.',
  },
  {
    icon: Code2,
    title: 'No vendor lock-in',
    body: 'Export your data in CSV or SQL whenever you want. Your case files, your clients, your time entries — they belong to your firm, not to us.',
  },
];

export default function AboutPage() {
  return (
    <main style={{ background: BRAND.bgWhite, color: BRAND.textBody }}>

      {/* ════════ HERO ════════ */}
      <section
        className="pt-16 lg:pt-24 pb-16 relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BRAND.bgWhite} 0%, ${BRAND.bgLight} 100%)` }}
      >
        <div
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] pointer-events-none"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${BRAND.blueGhost} 0%, transparent 55%)`,
            opacity: 0.6,
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{
              background: BRAND.orangeSoft,
              color: BRAND.orangeDark,
            }}
          >
            <Heart size={12} />
            About DocketFlow
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6"
            style={{ color: BRAND.textHead }}
          >
            Practice-management software, made in Ghana.
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed" style={{ color: BRAND.textMuted }}>
            We built DocketFlow because the existing options for Ghanaian law firms were
            either spreadsheets, WhatsApp groups, or US-priced subscriptions translated
            badly for emerging markets. None of those should be how a firm runs in 2026.
          </p>
        </div>
      </section>

      {/* ════════ STORY ════════ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
            >
              <BookOpen size={18} />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: BRAND.orangeDark }}>
              The story
            </p>
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-7"
            style={{ color: BRAND.textHead }}
          >
            We watched a senior partner re-key the same client name into five different
            systems. So we built one place to do all of it.
          </h2>
          <div className="space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: BRAND.textBody }}>
            <p>
              The first version of DocketFlow ran on a single attorney's laptop. He kept his
              case files in a spreadsheet, his time in a notebook, his deadlines in his calendar,
              and his bills in Word documents. Three days a week he asked his paralegal where
              a client file was. Twice a month the answer was "wait, I'll check my WhatsApp."
            </p>
            <p>
              We spent six months sitting with firms — solo practitioners, mid-size commercial
              shops, chambers attached to litigation practices — and watched them work. The
              software they were paying for was either built for an American practice (and
              priced like it), or it was a digital filing cabinet that didn't understand what
              a case file actually <em>is</em>.
            </p>
            <p>
              So we built DocketFlow. Case files that respect the way Ghanaian firms structure
              their work. Trust accounting that satisfies the Bar's three-way reconciliation
              requirement. Secure messaging that doesn't require giving WhatsApp your client
              communications. Billing in cedis, paid through Paystack, with VAT and NHIL
              lines built into every invoice.
            </p>
            <p>
              We're a small team. We answer support emails ourselves. We change the product
              when a customer tells us we got something wrong. We're not VC-backed, we're not
              trying to be a billion-dollar SaaS, and we're not going to make you sit through
              a 60-slide deck before we'll let you use the software.
            </p>
          </div>
        </div>
      </section>

      {/* ════════ VALUES ════════ */}
      <section className="py-20" style={{ background: BRAND.bgLight }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
              >
                <Compass size={18} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: BRAND.orangeDark }}>
                What we believe
              </p>
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ color: BRAND.textHead }}
            >
              The principles we build by.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {VALUES.map((v) => {
              const Ic = v.icon;
              return (
                <div
                  key={v.title}
                  className="p-7 rounded-2xl border"
                  style={{
                    background: BRAND.bgWhite,
                    borderColor: BRAND.border,
                    boxShadow: '0 8px 32px rgba(15,42,95,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-5"
                    style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
                  >
                    <Ic size={18} />
                  </div>
                  <h3 className="text-lg font-bold mb-2.5" style={{ color: BRAND.textHead }}>
                    {v.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════ WHERE WE'RE GOING ════════ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
            >
              <Sparkles size={18} />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: BRAND.orangeDark }}>
              Where we're going
            </p>
          </div>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-7"
            style={{ color: BRAND.textHead }}
          >
            We're just getting started.
          </h2>
          <div className="space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: BRAND.textBody }}>
            <p>
              Our 2026 roadmap is shaped by the firms using DocketFlow today: deeper court
              system integrations, mobile money for invoice payments, automated conflict
              checks against the bar's published case database, and AI-assisted document
              drafting for the boilerplate work that drains a junior associate's day.
            </p>
            <p>
              We'd rather build one thing well than ten things badly. If you'd like to help
              us decide what to build next, the best way is to{' '}
              <button
                onClick={() => goTo('bookDemo')}
                className="font-semibold underline-offset-4 hover:underline"
                style={{ color: BRAND.orangeDark }}
              >
                book a demo
              </button>
              {' '}and tell us about your practice.
            </p>
          </div>
        </div>
      </section>

      {/* ════════ CTA ════════ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl p-10 lg:p-14 text-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.blueDeep} 0%, ${BRAND.blueMid} 100%)`,
              color: '#fff',
              boxShadow: '0 24px 64px rgba(15,42,95,0.30)',
            }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
              Ready to take a look?
            </h2>
            <p
              className="text-base sm:text-lg max-w-xl mx-auto mb-8 leading-relaxed"
              style={{ color: 'rgba(226,232,240,0.85)' }}
            >
              Book a 20-minute demo and we'll show you how DocketFlow fits the way your
              firm actually practices.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => goTo('bookDemo')}
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-xl transition-all w-full sm:w-auto"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(249,115,22,0.40)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Book a demo
                <ArrowRight size={16} />
              </button>
              {/* Lower-commitment alternative for a visitor who's just
                  finished reading the About page and is still warming up. */}
              <WatchDemoButton variant="ghost" size="lg" label="Watch demo (30s)" />
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}
